import { useEffect, useState } from "react";
import { Drawer, Spin, Tooltip } from "antd";
import { Copy, Pencil } from "lucide-react";
import Toast from "pages/data-table-view/components/Toast";
import { fetchSaleById, updateSale } from "services/salesService";
import { formatBusinessDate } from "utils/businessDate";

const STATUS_STYLES = {
  completed: "bg-green-100 text-green-800",
  refunded: "bg-orange-100 text-orange-800",
  partially_refunded: "bg-yellow-100 text-yellow-800",
  failed: "bg-red-100 text-red-800",
};

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatLabel = (value) =>
  value
    ? value
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    : "-";

const formatDate = (value) => formatBusinessDate(value);

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      })
    : "-";

const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
      STATUS_STYLES[status] || "bg-gray-100 text-gray-800"
    }`}
  >
    {formatLabel(status)}
  </span>
);

const DetailRow = ({ label, value, mono }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-1 sm:gap-4 py-2.5 border-b border-gray-100 last:border-0">
    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
    <dd className={`text-sm text-gray-900 break-all ${mono ? "font-mono text-xs" : ""}`}>
      {value ?? "-"}
    </dd>
  </div>
);

const CopyableDetailRow = ({ label, value, onCopy }) => {
  const text = value != null && value !== "" ? String(value) : null;

  const handleCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      onCopy({ message: `${label} copied to clipboard`, type: "success" });
    } catch {
      onCopy({ message: "Failed to copy ID. Please try again.", type: "error" });
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-1 sm:gap-4 py-2.5 border-b border-gray-100 last:border-0">
      <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</dt>
      <dd className="text-sm break-all">
        {text ? (
          <Tooltip title="Click to copy">
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-start gap-1.5 text-left font-mono text-xs text-blue-700 hover:text-blue-900 hover:underline cursor-pointer"
            >
              <span className="break-all">{text}</span>
              <Copy size={12} className="flex-shrink-0 mt-0.5 opacity-60" aria-hidden />
            </button>
          </Tooltip>
        ) : (
          <span className="font-mono text-xs text-gray-900">-</span>
        )}
      </dd>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="border border-gray-200 rounded-lg overflow-hidden">
    <h3 className="px-4 py-2.5 bg-gray-50 text-sm font-semibold text-gray-800 border-b border-gray-200">
      {title}
    </h3>
    <dl className="px-4 py-1">{children}</dl>
  </section>
);

const formatMetadata = (metadata) => {
  if (metadata == null || metadata === "") return "{}";
  if (typeof metadata === "string") {
    try {
      return JSON.stringify(JSON.parse(metadata), null, 2);
    } catch {
      return metadata;
    }
  }
  return JSON.stringify(metadata, null, 2);
};

const useDrawerWidth = () => {
  const [width, setWidth] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 768 ? "100%" : 520
  );

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth < 768 ? "100%" : 520);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return width;
};

export default function SalesDetailsDrawer({ open, saleId, api, onClose, onUpdated }) {
  const drawerWidth = useDrawerWidth();
  const [sale, setSale] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [isEditingDeliveryCost, setIsEditingDeliveryCost] = useState(false);
  const [draftDeliveryCost, setDraftDeliveryCost] = useState("");
  const [isSavingDeliveryCost, setIsSavingDeliveryCost] = useState(false);

  const showToast = ({ message, type }) => setToast({ show: true, message, type });

  const resetDeliveryCostEdit = () => {
    setIsEditingDeliveryCost(false);
    setDraftDeliveryCost("");
    setIsSavingDeliveryCost(false);
  };

  useEffect(() => {
    if (!open || !saleId) {
      setSale(null);
      setError(null);
      resetDeliveryCostEdit();
      return;
    }

    let cancelled = false;

    const loadSale = async () => {
      setIsLoading(true);
      setError(null);
      resetDeliveryCostEdit();
      try {
        const data = await fetchSaleById(api, saleId);
        if (!cancelled) setSale(data);
      } catch {
        if (!cancelled) setError("Failed to load sale details.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadSale();
    return () => {
      cancelled = true;
    };
  }, [open, saleId, api]);

  const startEditDeliveryCost = () => {
    setDraftDeliveryCost(
      sale?.deliveryCost != null ? String(sale.deliveryCost) : "0"
    );
    setIsEditingDeliveryCost(true);
  };

  const cancelEditDeliveryCost = () => {
    setDraftDeliveryCost(
      sale?.deliveryCost != null ? String(sale.deliveryCost) : "0"
    );
    setIsEditingDeliveryCost(false);
  };

  const saveDeliveryCost = async () => {
    if (!sale?._id || isSavingDeliveryCost) return;

    const nextValue = Number(draftDeliveryCost);
    if (Number.isNaN(nextValue) || nextValue < 0) {
      showToast({ message: "Delivery cost must be 0 or greater.", type: "error" });
      return;
    }

    setIsSavingDeliveryCost(true);
    try {
      const updated = await updateSale(api, sale._id, { deliveryCost: nextValue });
      setSale(updated);
      setIsEditingDeliveryCost(false);
      onUpdated?.(sale._id, {
        deliveryCost: updated?.deliveryCost ?? nextValue,
        updatedAt: updated?.updatedAt,
      });
      showToast({ message: "Delivery cost updated successfully.", type: "success" });
    } catch {
      showToast({ message: "Failed to update delivery cost.", type: "error" });
    } finally {
      setIsSavingDeliveryCost(false);
    }
  };

  return (
    <>
      <Drawer
        title="Sale Details"
        placement="right"
        open={open}
        onClose={onClose}
        width={drawerWidth}
        destroyOnClose
      >
      {isLoading && (
        <div className="flex justify-center py-12">
          <Spin />
        </div>
      )}

      {!isLoading && error && (
        <p className="text-sm text-red-600 text-center py-8">{error}</p>
      )}

      {!isLoading && !error && sale && (
        <div className="space-y-4 pb-4">
          <Section title="Sale Information">
            <DetailRow label="Sale Date" value={formatDate(sale.saleDate)} />
            <DetailRow label="Product Name" value={sale.productName || "-"} />
            <DetailRow label="Qty" value={sale.qty ?? "-"} />
            <DetailRow label="Unit Price" value={formatCurrency(sale.unitPrice)} />
            <DetailRow label="Gross Amount" value={formatCurrency(sale.grossAmount)} />
            <DetailRow label="Total Amount" value={formatCurrency(sale.totalAmount)} />
            <DetailRow label="Refund Amount" value={formatCurrency(sale.refundAmount)} />
            <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-1 sm:gap-4 py-2.5 border-b border-gray-100 last:border-0">
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Delivery Cost
              </dt>
              <dd className="text-sm text-gray-900">
                {isEditingDeliveryCost ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={draftDeliveryCost}
                      onChange={(e) => setDraftDeliveryCost(e.target.value)}
                      disabled={isSavingDeliveryCost}
                      className="w-28 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                      aria-label="Delivery cost"
                    />
                    <button
                      type="button"
                      onClick={saveDeliveryCost}
                      disabled={isSavingDeliveryCost}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingDeliveryCost ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEditDeliveryCost}
                      disabled={isSavingDeliveryCost}
                      className="text-sm font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <span>{formatCurrency(sale.deliveryCost)}</span>
                    <Tooltip title="Edit delivery cost">
                      <button
                        type="button"
                        onClick={startEditDeliveryCost}
                        className="text-gray-400 hover:text-blue-600"
                        aria-label="Edit delivery cost"
                      >
                        <Pencil size={14} />
                      </button>
                    </Tooltip>
                  </div>
                )}
              </dd>
            </div>
            {sale.grossProfit != null && (
              <DetailRow
                label="Gross Profit"
                value={formatCurrency(sale.grossProfit)}
              />
            )}
            <DetailRow label="Status" value={<StatusBadge status={sale.status} />} />
            <DetailRow label="Sale Type" value={formatLabel(sale.saleType)} />
            <DetailRow label="Source" value={formatLabel(sale.source)} />
            <DetailRow label="UTM Source" value={sale.utm_source || "-"} />
          </Section>

          <Section title="Stripe Information">
            <CopyableDetailRow label="Payment Intent ID" value={sale.stripePaymentIntentId} onCopy={showToast} />
            <CopyableDetailRow label="Charge ID" value={sale.stripeChargeId} onCopy={showToast} />
            <CopyableDetailRow label="Checkout Session ID" value={sale.stripeCheckoutSessionId} onCopy={showToast} />
            <CopyableDetailRow label="Customer ID" value={sale.stripeCustomerId} onCopy={showToast} />
            <CopyableDetailRow label="Product ID" value={sale.stripeProductId} onCopy={showToast} />
            <CopyableDetailRow label="Price ID" value={sale.stripePriceId} onCopy={showToast} />
            <CopyableDetailRow label="Event ID" value={sale.stripeEventId} onCopy={showToast} />
          </Section>

          <section className="border border-gray-200 rounded-lg overflow-hidden">
            <h3 className="px-4 py-2.5 bg-gray-50 text-sm font-semibold text-gray-800 border-b border-gray-200">
              Metadata
            </h3>
            <pre className="m-0 p-4 text-xs font-mono text-gray-800 bg-gray-50 overflow-x-auto max-h-64 overflow-y-auto whitespace-pre-wrap break-words">
              {formatMetadata(sale.metadata)}
            </pre>
          </section>

          <Section title="Audit Information">
            <DetailRow label="Created At" value={formatDateTime(sale.createdAt)} />
            <DetailRow label="Updated At" value={formatDateTime(sale.updatedAt)} />
          </Section>
        </div>
      )}
      </Drawer>
      {toast.show && (
        <Toast
          key={toast.message}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
        />
      )}
    </>
  );
}
